// @flow

import * as React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { push } from "connected-react-router";
import { Helmet } from "react-helmet";
import { ToastContainer } from "react-toastify";
import { getCurrentPath, getRouteParams } from "@selectors";
import { getClientSettings } from "@modules/settings/selectors";
import { getClientContext } from "@modules/context/selectors";
import config from "@config";
import { formatDateTime } from "@transformers/date";
import { withPermissions } from "@modules/account/components";
import { getPermissions, getUserType, getUser } from "@modules/account/selectors";
import Sidebar from "../navigation/sidebar";
import { ChangeLog, View } from "@components";
import AppHeader from "../navigation/app-header";

import "./dashboard.style.scss";
import {
  getUserId,
  isClientAdmin,
  hasDefaultClient,
  getIsImpersonateLogin,
  getDisplayName,
} from "@modules/account/selectors";

import { withSettings } from "@modules/settings/components";
import clientsActionCreators from "@modules/client/actionCreator";
import accountActionCreators from "@modules/account/actionCreator";
import type { Client } from "@modules/context/types";
import type { ProfileUser } from "@modules/account/types";
import LocalStorageService from "@services/LocalStorageService";
import moment from "moment";
import sharedActionCreators from "@modules/shared/actionCreator";

type OwnProps = $Exact<{
  currentUserId: number | null,
  isClientAdmin: boolean,
  hasDefaultClient: boolean,
  clientId: number | null,
  hasPermission: *,
  hasAccess: *,
  clientSettings: Object, // Coming from "withPermission
  extraProps: Object,
  hideSidebar?: boolean,
  hideNavigation?: boolean,
  currentUserType?: string,
  releaseNote: Object | null,
  lastThreeSuccessfulLogins: Array<string>,
}>;

type PropsFromState = $Exact<{
  title: string,
  contentId: ?number,
  employeeId: ?number,
  children: React.Node,
  location: string,
  breadCrumbs?: any,
  changelog?: $ElementType<React.ElementProps<typeof ChangeLog>, "entries">,
  routeParams: Object,
  isImpersonateLogin: boolean,
  usersDisplayName: string,
  clientSettings: Object,
  client: Client | null,
  user: ProfileUser,
  permissions: { [permission: string]: boolean },
}>;

type PropsFromDispatch = $Exact<{
  navigate: Function,
  permissionId?: string,
  getClientService: typeof clientsActionCreators.GetServiceAbsenceManagementSettings,
  toggleContactModal: typeof accountActionCreators.toggleContactModal,
}>;

type State = $Exact<{
  enableScrollToTopButton: boolean,
  isCollapsed: boolean,
  showReleaseNotes: boolean,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class DashboardLayout extends React.Component<Props, State> {
  state = {
    enableScrollToTopButton: false,
    isCollapsed: LocalStorageService.getItem("sidebarIsCollapsed") || false,
    showReleaseNotes: false,
  };

  componentDidMount() {
    if (this.props.clientId) {
      this.props.getClientService({
        clientId: this.props.clientId,
      });
    }
    const mainContent = document.querySelector("#main-content");
    //$FlowIgnore
    mainContent && mainContent.addEventListener("scroll", this.onScroll);
  }

  componentDidUpdate(prevProps: Props): * {
    if (this.props.releaseNote !== prevProps.releaseNote && this.shouldDisplayNewReleaseNotes) {
      this.setState({
        showReleaseNotes: true,
      });
    }
  }

  onScroll = (event: WheelEvent) => {
    this.setState({
      ...this.state, //$FlowIgnore
      enableScrollToTopButton: !!(event.target.scrollTop > 100),
    });
  };
  scrollToTop = () => {
    const mainContent = document.querySelector("#main-content");
    if (mainContent) {
      //$FlowIgnore
      mainContent.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  toggleContactModal = () => {
    this.props.toggleContactModal();
  };

  navigate = (path: string) => {
    this.props.navigate(path);
  };

  get clientId() {
    if (this.props.clientId) return this.props.clientId;
    else if (this.props.hasDefaultClient) return this.props.user.client.id;
    else this.props.clientId;
  }

  toggleSidebarHandler = () => {
    const { isCollapsed } = this.state;

    LocalStorageService.setItem("sidebarIsCollapsed", !isCollapsed);

    this.setState({
      ...this.state,
      isCollapsed: !isCollapsed,
    });
  };

  hideNewReleaseNotes = () => {
    this.setState({
      showReleaseNotes: false,
    });
  };

  get shouldDisplayNewReleaseNotes() {
    const { releaseNote } = this.props;
    const lastThreeSuccessfulLogins = this.props.lastThreeSuccessfulLogins || [];
    let show = false;

    const releaseNoteDate = moment(releaseNote?.createdAt);

    // define last successful login time
    const now = new Date();
    const _30sec = 30000; // in milliseconds
    const zeroKey = new Date(lastThreeSuccessfulLogins[0]);

    // if last login is made in the range of last 30sec, should use to the previous successful login
    const lastLoginDate = moment(
      now.getTime() - zeroKey.getTime() < _30sec
        ? lastThreeSuccessfulLogins[1]
        : lastThreeSuccessfulLogins[0],
    );

    // If the release date is after last successful login, then show the message
    if (releaseNoteDate.isAfter(lastLoginDate)) {
      show = true;
    }

    return show;
  }

  downloadReleaseNotes = () => {
    this.props.getReleaseNotesDocument({
      fileName: `${this.props.t("VERSION_CONTROL_NOTES_TITLE")} ${this.props.releaseNote.version ||
        "??"}.pdf`,
    });
  };

  render() {
    const clientId = this.clientId;
    return (
      <View
        name="DashboardLayout"
        maxWidth="100vw"
        maxHeight="100vh"
        className={[this.state.isCollapsed && "collapsed"]}
      >
        <Helmet>
          <meta charSet="utf-8" />
          <title>{this.props.title}</title>
        </Helmet>
        {this.props.isImpersonateLogin && (
          <div className="impersonate-notification">
            {this.props.t("IMPERSONATION_NOTIFICATION", {
              name: this.props.usersDisplayName,
            })}
          </div>
        )}

        <View className="dashboard-layout" direction="row" overflow="hidden">
          {this.props.permissions && (
            <Sidebar
              navigate={this.navigate}
              currentPath={this.props.location}
              permissions={this.props.permissions}
              clientSettings={this.props.clientSettings}
              clientId={clientId}
              employeeId={this.props.employeeId}
              user={this.props.user}
              isCollapsed={this.state.isCollapsed}
            />
          )}

          <View flex={1} id="content">
            <AppHeader
              navigate={this.navigate}
              currentPath={this.props.location}
              breadCrumbs={this.props.breadCrumbs}
              toggleSidebar={this.toggleSidebarHandler}
              isCollapsed={this.state.isCollapsed}
            />
            <View
              id="main-content"
              className={[this.state.isCollapsed && "collapsed"]}
              padding={[0, 6, 6, 6]}
              gap={6}
              flex={1}
              name={"MainContent"}
            >
              {this.props.children}
              {this.props.changelog && (
                <ChangeLog
                  entries={this.props.changelog}
                  t={this.props.t}
                  formatDate={formatDateTime}
                />
              )}
              <span
                onClick={this.scrollToTop}
                className={`scroll-to-top d-print-none material-icons-outlined ${
                  this.state.enableScrollToTopButton ? "show" : ""
                }`}
              >
                expand_less
              </span>
            </View>
          </View>
        </View>

        <ToastContainer
          position="bottom-left"
          type="default"
          autoClose={config.toasterShowTime}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          pauseOnHover
        />
      </View>
    );
  }
}

const LocalisedDashboardLayout = localize()(DashboardLayout);

const mapStateToProps = (state, props) => {
  const routeParams = getRouteParams(props.extraProps) || {};
  const user = getUser(state);
  return {
    routeParams,
    employeeId: routeParams.employeeId,
    clientId: routeParams.clientId,
    location: getCurrentPath(state),
    currentUserId: getUserId(state),
    isClientAdmin: isClientAdmin(state),
    hasDefaultClient: hasDefaultClient(state),
    isImpersonateLogin: getIsImpersonateLogin(state),
    usersDisplayName: getDisplayName(state),
    client: getClientContext(state),
    permissions: getPermissions(state),
    clientSettings: getClientSettings(state),
    currentUserType: getUserType(state),
    user,
  };
};

const mapDispatchToProps = (dispatch: Function) => {
  return {
    getClientService: clientsActionCreators.GetServiceAbsenceManagementSettings,
    navigate: path => dispatch(push(path)),
    toggleContactModal: () => dispatch(accountActionCreators.toggleContactModal()),
  };
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withSettings(),
  withPermissions({}),
)(LocalisedDashboardLayout);
